import { isEphemeralComponentName } from "@/src/features/windows/ephemeralComponents";
import { isRetiredComponentName } from "@/src/features/windows/retiredComponents";
import type { componentsMapType } from "@/src/shared/contracts/app";

const MAX_SEARCH_LABELS = 18;
const MAX_SEARCH_LABEL_LENGTH = 180;
const MAX_SEARCH_TEXT_LENGTH = 900;

const SEARCHABLE_VALUE_KEYS = new Set([
  "title",
  "subject",
  "name",
  "componentName",
  "filename",
  "fileName",
  "label",
  "description",
  "reason",
  "sender",
  "senderName",
  "from",
  "email",
  "url",
  "type",
  "fileType",
  "status",
  "platform",
  "provider",
  "calendarView",
  "view",
  "selectDate",
  "date",
  "createdDate",
  "updatedDate",
]);

const JSON_CONTAINER_KEYS = new Set(["rawData", "data"]);
const NON_REOPENABLE_COMPONENT_NAMES = new Set([
  "NeuralCanvas",
  "Nav",
  "SubscriptionRequired",
]);

export type SavedComponentDocument = {
  componentId?: unknown;
  componentName?: unknown;
  reason?: unknown;
  description?: unknown;
  createdDate?: unknown;
  updatedDate?: unknown;
  componentPayload?: unknown;
};

export type ClosedComponentCandidate = {
  component: componentsMapType;
  manifest: {
    componentId: string;
    componentName: string;
    title: string | null;
    createdAt: number | null;
    lastSavedAt: number | null;
    closedAt: number | null;
    searchText: string;
    stateKeys: string[];
    parentComponentId: string | null;
    dependencyIds: string[];
  };
};

function asRecord(value: unknown): Record<string, any> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, any>
    : null;
}

function toTimestamp(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }
  if (typeof value !== "string" || !value.trim()) return null;
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) return numeric;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function truncateLabel(value: string): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized.length <= MAX_SEARCH_LABEL_LENGTH
    ? normalized
    : `${normalized.slice(0, MAX_SEARCH_LABEL_LENGTH - 3).trim()}...`;
}

function sanitizeSearchableString(parentKey: string, value: string): string {
  if (parentKey !== "url") return value;
  try {
    const url = new URL(value);
    return `${url.origin}${url.pathname}`;
  } catch {
    return value.split(/[?#]/, 1)[0];
  }
}

function collectSearchLabels(
  value: unknown,
  labels: Set<string>,
  depth = 0,
  parentKey = "",
): void {
  if (labels.size >= MAX_SEARCH_LABELS || depth > 8 || value === null || value === undefined) {
    return;
  }

  if (typeof value === "string") {
    if (JSON_CONTAINER_KEYS.has(parentKey)) {
      try {
        collectSearchLabels(JSON.parse(value), labels, depth + 1, parentKey);
      } catch {
        // Free-form bodies and code are intentionally excluded from the
        // semantic candidate manifest.
      }
      return;
    }
    if (!SEARCHABLE_VALUE_KEYS.has(parentKey)) return;
    const label = truncateLabel(sanitizeSearchableString(parentKey, value));
    if (label) labels.add(label);
    return;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    if (SEARCHABLE_VALUE_KEYS.has(parentKey)) labels.add(String(value));
    return;
  }

  if (Array.isArray(value)) {
    for (const item of value.slice(0, 12)) {
      collectSearchLabels(item, labels, depth + 1, parentKey);
      if (labels.size >= MAX_SEARCH_LABELS) break;
    }
    return;
  }

  const record = asRecord(value);
  if (!record) return;
  for (const [key, child] of Object.entries(record)) {
    if (SEARCHABLE_VALUE_KEYS.has(key) || JSON_CONTAINER_KEYS.has(key)) {
      collectSearchLabels(child, labels, depth + 1, key);
    } else if (depth < 7 && child && typeof child === "object") {
      collectSearchLabels(child, labels, depth + 1, key);
    }
    if (labels.size >= MAX_SEARCH_LABELS) break;
  }
}

function isValidComponentSize(value: unknown): value is { width: number; height: number } {
  const size = asRecord(value);
  return Boolean(
    size
    && typeof size.width === "number"
    && Number.isFinite(size.width)
    && size.width > 0
    && size.width <= 10_000
    && typeof size.height === "number"
    && Number.isFinite(size.height)
    && size.height > 0
    && size.height <= 10_000,
  );
}

function isValidComponentPosition(value: unknown): value is { x: number; y: number } {
  const position = asRecord(value);
  return Boolean(
    position
    && typeof position.x === "number"
    && Number.isFinite(position.x)
    && typeof position.y === "number"
    && Number.isFinite(position.y),
  );
}

function isReopenableComponentName(name: string): boolean {
  return (
    /^[A-Za-z][A-Za-z0-9 _-]{0,127}$/.test(name)
    && !NON_REOPENABLE_COMPONENT_NAMES.has(name)
    && !isEphemeralComponentName(name)
    && !isRetiredComponentName(name)
  );
}

export function isSavedComponentClosed(value: unknown): boolean {
  const component = asRecord(value);
  const deletedDate = component?.deletedDate;
  const hasDeletedDate = typeof deletedDate === "string"
    ? deletedDate.trim().length > 0
    : typeof deletedDate === "number"
      ? Number.isFinite(deletedDate) && deletedDate > 0
      : deletedDate != null;
  return Boolean(
    component
    && (
      hasDeletedDate
      || component.isDeleted === true
      || component.deleted === true
      || component.status === "deleted"
    ),
  );
}

function unwrapSavedComponentDocument(value: unknown): SavedComponentDocument | null {
  const record = asRecord(value);
  if (!record) return null;
  return asRecord(record._source) || record;
}

export function normalizeReopenedComponent(value: unknown): componentsMapType | null {
  const outer = asRecord(value);
  if (!outer) return null;

  let candidate: Record<string, any> | null = asRecord(outer.reopenedComponent);
  if (!candidate && typeof outer.data === "string") {
    try {
      const parsed = JSON.parse(outer.data);
      candidate = asRecord(parsed?.reopenedComponent) || asRecord(parsed?.componentPayload);
    } catch {
      candidate = null;
    }
  }
  candidate ||= asRecord(outer.componentPayload) || outer;

  const id = typeof candidate.id === "string" ? candidate.id.trim() : "";
  const name = typeof candidate.name === "string" ? candidate.name.trim() : "";
  if (
    !id
    || !name
    || !isReopenableComponentName(name)
    || !isValidComponentSize(candidate.size)
  ) {
    return null;
  }

  const props = asRecord(candidate.props) || {};
  const position = isValidComponentPosition(candidate.position)
    ? { x: candidate.position.x, y: candidate.position.y }
    : { x: 20, y: 20 };

  return {
    ...candidate,
    id,
    name,
    position,
    size: { width: candidate.size.width, height: candidate.size.height },
    priority: typeof candidate.priority === "string" && candidate.priority.trim()
      ? candidate.priority
      : "Mid",
    group: typeof candidate.group === "string" ? candidate.group : null,
    props: { ...props, id },
    createdDate: candidate.createdDate ?? new Date(),
    deletedDate: null,
    isMinimized: false,
    minimizedScale: undefined,
    isAnimatingPosition: undefined,
    animationStartTime: undefined,
    isRestoringFromMinimized: undefined,
    restorationStartTime: undefined,
  } as componentsMapType;
}

function getCandidateTitle(component: componentsMapType, labels: Set<string>): string | null {
  const props = asRecord(component.props) || {};
  const candidates = [
    props.title,
    props.subject,
    props.filename,
    props.fileName,
    component.description,
    component.reason,
    ...Array.from(labels),
  ];
  const title = candidates.find((value) => typeof value === "string" && value.trim());
  return typeof title === "string" ? truncateLabel(title) : null;
}

export function buildClosedComponentCandidates(
  documents: unknown[],
  activeComponentIds: Iterable<string>,
): ClosedComponentCandidate[] {
  const activeIds = new Set(Array.from(activeComponentIds, (id) => String(id).trim()).filter(Boolean));
  const candidates = new Map<string, ClosedComponentCandidate>();

  for (const value of Array.isArray(documents) ? documents : []) {
    const document = unwrapSavedComponentDocument(value);
    const payload = normalizeReopenedComponent(document?.componentPayload);
    if (!document || !payload || activeIds.has(payload.id)) continue;

    const labels = new Set<string>();
    collectSearchLabels({
      componentName: payload.name,
      title: payload.description,
      reason: payload.reason,
      props: payload.props,
    }, labels);
    labels.add(payload.name);

    const createdAt = toTimestamp(document.createdDate) ?? toTimestamp(payload.createdDate);
    const lastSavedAt = toTimestamp(document.updatedDate) ?? createdAt;
    const closedAt = toTimestamp((document.componentPayload as any)?.deletedDate) ?? lastSavedAt;
    const searchText = Array.from(labels).join(" | ").slice(0, MAX_SEARCH_TEXT_LENGTH);
    const stateKeys = Object.keys(asRecord(payload.props) || {})
      .filter((key) => !/(?:token|secret|password|authorization|apiKey|credential|code|body|content)/i.test(key))
      .slice(0, 20);
    const candidate = {
      component: payload,
      manifest: {
        componentId: payload.id,
        componentName: payload.name,
        title: getCandidateTitle(payload, labels),
        createdAt,
        lastSavedAt,
        closedAt,
        searchText,
        stateKeys,
        parentComponentId: typeof payload.parentComponentId === "string"
          ? payload.parentComponentId
          : null,
        dependencyIds: Array.isArray(payload.dependencyIds)
          ? payload.dependencyIds.filter((id): id is string => typeof id === "string").slice(0, 20)
          : [],
      },
    };

    const existing = candidates.get(payload.id);
    if (!existing || (candidate.manifest.lastSavedAt || 0) > (existing.manifest.lastSavedAt || 0)) {
      candidates.set(payload.id, candidate);
    }
  }

  return Array.from(candidates.values()).sort(
    (left, right) => (right.manifest.closedAt || 0) - (left.manifest.closedAt || 0),
  );
}
